import React, { useState, useRef, useEffect } from 'react';
import { Camera, ImagePlus, Loader2, AlertTriangle, CheckCircle, RefreshCcw, Info, Upload, X, SwitchCamera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSkinAnalyzer } from '@/hooks/use-skin-analyzer';
import { useCameraHandler } from '@/hooks/use-camera-handler';
import { useImageHandler } from '@/hooks/use-image-handler';
import { SkinAnalysisResult } from '@/types/skin-analysis';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const SkinAnalysisSection = () => {
  const {
    image: imagePreview,
    isProcessing: isProcessingImage,
    error: imageError,
    handleFileChange,
    captureFromVideo,
    resetImage
  } = useImageHandler({ thumbnailWidth: 800 });
  
  const [skinArea, setSkinArea] = useState<string>('non spécifié');
  const [symptoms, setSymptoms] = useState<string[]>([]);
  
  const {
    devices,
    selectedDeviceId,
    stream,
    error: cameraError,
    isLoading: isCameraLoading,
    isListingDevices,
    hasCameraSupport,
    activeCamera,
    facingMode,
    startCamera,
    stopCamera,
    switchCamera,
    toggleCamera,
    retry: enumerateDevices
  } = useCameraHandler();
  const [showCameraSelector, setShowCameraSelector] = useState<boolean>(false);
  const [showVideoContainer, setShowVideoContainer] = useState<boolean>(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { isAnalyzing, result, apiError, analyzeImage } = useSkinAnalyzer();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.error("Erreur de lecture vidéo:", err);
      });
    }
  }, [stream]);
  
  const captureImage = async () => {
    if (!videoRef.current) {
      toast.error("Caméra non disponible", {
        description: "Impossible d'accéder à la caméra pour la capture d'image"
      });
      return;
    }
    
    const success = await captureFromVideo(videoRef.current);
    
    if (success) {
      stopCamera();
      
      // Reset parameters
      setSkinArea('non spécifié');
      setSymptoms([]);
      
      toast.success("Image capturée avec succès", {
        description: "L'image est prête pour l'analyse"
      });
    }
  };
  
  const analyzeCapture = async () => {
    if (!imagePreview) {
      toast.error("Aucune image", { 
        description: "Veuillez capturer ou importer une image d'abord" 
      });
      return;
    }
    
    try {
      await analyzeImage(imagePreview, skinArea, symptoms);
    } catch (error: any) {
      console.error('Erreur lors de l\'analyse de l\'image:', error);
      toast.error('Erreur d\'analyse', {
        description: error.message || 'Impossible d\'analyser l\'image. Veuillez réessayer.'
      });
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const resetAnalysis = () => {
    resetImage();
    setSkinArea('non spécifié');
    setSymptoms([]);
  };
  
  const openInBrowser = () => {
    const currentUrl = window.location.href;
    window.open(currentUrl, '_system');
  };

  const handleDeviceChange = (deviceId: string) => {
    switchCamera(deviceId);
    setShowCameraSelector(false);
  };

  const toggleCameraSelector = () => {
    setShowCameraSelector(!showCameraSelector);
  };
  
  const formatResult = (result: SkinAnalysisResult) => {
    const consultationMessage = result.consultationNeeded
      ? `Il est ${result.consultationUrgency === 'urgente' ? 'urgent' : 'recommandé'} de consulter un médecin.`
      : 'Aucune consultation médicale n\'est nécessaire pour le moment.';
    
    return (
      <>
        <p><strong>Type:</strong> {result.type}</p>
        <p><strong>Sévérité:</strong> {result.severity}</p>
        <p><strong>Description:</strong> {result.description || 'N/A'}</p>
        {result.differentialDiagnosis && result.differentialDiagnosis.length > 0 && (
          <p><strong>Diagnostic différentiel:</strong> {result.differentialDiagnosis.join(', ')}</p>
        )}
        {result.potentialCauses && result.potentialCauses.length > 0 && (
          <p><strong>Causes potentielles:</strong> {result.potentialCauses.join(', ')}</p>
        )}
        <p><strong>Recommandations:</strong> {result.recommendations.join(', ')}</p>
        <p><strong>Consultation:</strong> {consultationMessage}</p>
        <p><strong>Pronostic:</strong> {result.prognosis || 'N/A'}</p>
        {result.complications && result.complications.length > 0 && (
          <p><strong>Complications possibles:</strong> {result.complications.join(', ')}</p>
        )}
      </>
    );
  };
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-blue-600 p-2 rounded-full text-white">
          <Camera className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Analyse de peau et blessures</h3>
          <p className="text-gray-600 text-sm mb-3">
            Prenez une photo de votre peau ou d'une blessure pour obtenir une analyse préliminaire et des recommandations.
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
        <p className="text-xs text-amber-800">
          Cette analyse ne remplace pas un diagnostic médical professionnel. 
          Consultez un médecin pour toute préoccupation de santé.
        </p>
      </div>
      
      {(cameraError || apiError || imageError) && !imagePreview && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-4">
          <X className="h-4 w-4 text-red-500 shrink-0" />
          <div>
            <p className="text-xs text-red-800">
              {imageError || apiError || cameraError}
            </p>
            {cameraError && (
              <Button
                variant="link"
                size="sm"
                className="text-xs p-0 h-auto mt-1 text-red-700"
                onClick={openInBrowser}
              >
                Ouvrir dans le navigateur par défaut
              </Button>
            )}
          </div>
        </div>
      )}
      
      {!imagePreview && (
        <div className="space-y-4">
          {showVideoContainer && (
            <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border border-gray-200" style={{ minHeight: '300px' }}>
            {stream ? (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                playsInline
                muted
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center p-4">
                  {isListingDevices ? (
                    <>
                      <Loader2 className="h-12 w-12 text-gray-400 mx-auto mb-2 animate-spin" />
                      <p className="text-gray-500 text-sm">Détection des caméras...</p>
                    </>
                  ) : (
                    <>
                      <ImagePlus className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">
                        {hasCameraSupport
                          ? "Activez la caméra ou importez une image"
                          : "Importez une image pour analyse"}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {activeCamera && (
              <div className="absolute bottom-3 right-3 flex gap-2">
                <Button
                  onClick={toggleCameraSelector}
                  size="sm"
                  variant="secondary"
                  className="bg-white/80 backdrop-blur-sm hover:bg-white"
                  disabled={isCameraLoading}
                >
                  <Camera className="h-4 w-4 mr-1" />
                  {isCameraLoading ? 'Chargement...' : 'Changer de caméra'}
                </Button>
                <Button
                  onClick={toggleCamera}
                  size="sm"
                  variant="secondary"
                  className="bg-white/80 backdrop-blur-sm hover:bg-white"
                >
                  <SwitchCamera className="h-4 w-4" />
                </Button>
              </div>
            )}

            {showCameraSelector && devices.length > 0 && (
              <div className="absolute top-3 left-3 right-3 z-10">
                <div className="bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow border border-gray-200">
                  <h4 className="text-sm font-medium mb-2 text-gray-800">Sélectionner une caméra</h4>
                  <Select 
                    value={selectedDeviceId} 
                    onValueChange={handleDeviceChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sélectionner une caméra" />
                    </SelectTrigger>
                    <SelectContent>
                      {devices.map((device) => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>
                          {device.label || `Caméra ${device.deviceId.substring(0, 4)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex justify-end mt-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs"
                      onClick={() => setShowCameraSelector(false)}
                    >
                      Fermer
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {activeCamera ? (
              <>
                <Button 
                  onClick={captureImage} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={isProcessingImage}
                >
                  {isProcessingImage ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <Camera className="h-4 w-4 mr-2" />
                      Capturer une image
                    </>
                  )}
                </Button>
                <Button 
                  onClick={stopCamera} 
                  variant="outline" 
                  className="flex-1"
                  disabled={isProcessingImage}
                >
                  <X className="h-4 w-4 mr-2" />
                  Arrêter la caméra
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={isListingDevices || !hasCameraSupport || isProcessingImage}
                  onClick={() => {
                    startCamera(selectedDeviceId);
                    setShowVideoContainer(true);
                  }}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Activer la caméra
                </Button>
                <div className="flex-1">
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleImportClick}
                    disabled={isProcessingImage}
                  >
                    {isProcessingImage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Traitement...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Importer une image
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {imagePreview && !result && !apiError && (
        <div className="space-y-4">
          <div className="relative aspect-video rounded-lg overflow-hidden border border-gray-200">
            <img 
              src={imagePreview} 
              alt="Prévisualisation" 
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zone de peau
              </label>
              <select
                value={skinArea}
                onChange={(e) => setSkinArea(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
              >
                <option value="non spécifié">Sélectionnez</option>
                <option value="visage">Visage</option>
                <option value="bras">Bras</option>
                <option value="jambes">Jambes</option>
                <option value="torse">Torse</option>
                <option value="mains">Mains</option>
                <option value="pieds">Pieds</option>
                <option value="cuir chevelu">Cuir chevelu</option>
                <option value="dos">Dos</option>
                <option value="cou">Cou</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Symptômes (optionnel)
              </label>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value && !symptoms.includes(e.target.value)) {
                    setSymptoms([...symptoms, e.target.value]);
                  }
                }}
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
              >
                <option value="">Sélectionnez</option>
                <option value="démangeaisons">Démangeaisons</option>
                <option value="douleur">Douleur</option>
                <option value="rougeur">Rougeur</option>
                <option value="gonflement">Gonflement</option>
                <option value="chaleur">Chaleur</option>
                <option value="saignement">Saignement</option>
                <option value="desquamation">Desquamation (peau qui pèle)</option>
                <option value="sécheresse">Sécheresse</option>
                <option value="croûtes">Croûtes</option>
                <option value="pus">Pus ou écoulement</option>
                <option value="vésicules">Vésicules ou bulles</option>
                <option value="brûlure">Sensation de brûlure</option>
              </select>
              
              {symptoms.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {symptoms.map((symptom, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                    >
                      {symptom}
                      <button
                        type="button"
                        onClick={() => setSymptoms(symptoms.filter((_, i) => i !== index))}
                        className="ml-1 text-blue-400 hover:text-blue-600 focus:outline-none"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={analyzeCapture}
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                'Analyser l\'image'
              )}
            </Button>
            <Button 
              onClick={resetAnalysis} 
              variant="outline" 
              className="flex-1"
            >
              Recommencer
            </Button>
          </div>
        </div>
      )}
      
      {(result && !apiError) && (
        <div className="mt-4 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-md overflow-hidden flex-shrink-0">
              <img 
                src={imagePreview || ''} 
                alt="Image analysée" 
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-1">
                <h4 className="font-semibold text-gray-900">
                  {result.type || "Condition détectée"}
                </h4>
                <div className={cn(
                  "px-2 py-0.5 text-xs rounded-full ml-2",
                  result.severity === "léger" && "bg-green-100 text-green-800",
                  result.severity === "modéré" && "bg-yellow-100 text-yellow-800",
                  result.severity === "sévère" && "bg-red-100 text-red-800"
                )}>
                  {result.severity || "Non évaluée"}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-2">
                {result.description || "Aucune description disponible"}
              </p>
              
              <div className="flex items-center text-xs text-gray-500">
                <Info className="h-3 w-3 mr-1" />
                <span>Zone: {skinArea || "Non spécifiée"}</span>
                {symptoms.length > 0 && (
                  <span className="ml-2">• Symptômes: {symptoms.join(', ')}</span>
                )}
              </div>
            </div>
          </div>
          
          {result.potentialCauses && result.potentialCauses.length > 0 && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <h5 className="text-sm font-medium text-gray-800 mb-2 flex items-center">
                <Info className="h-4 w-4 mr-1.5" />
                Causes potentielles
              </h5>
              <ul className="space-y-1">
                {result.potentialCauses.map((cause, index) => (
                  <li key={index} className="text-xs text-gray-700 flex items-start">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400 mt-1.5 mr-2"></span>
                    {cause}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {result.recommendations && result.recommendations.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
              <h5 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                <CheckCircle className="h-4 w-4 mr-1.5" />
                Recommandations
              </h5>
              <ul className="space-y-1">
                {result.recommendations.map((rec, index) => (
                  <li key={index} className="text-xs text-blue-700 flex items-start">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400 mt-1.5 mr-2"></span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {result.consultationNeeded && (
            <div className={cn(
              "rounded-lg border p-3",
              result.consultationUrgency === "urgente" ? "border-red-100 bg-red-50" :
              result.consultationUrgency === "semi-urgente" ? "border-amber-100 bg-amber-50" :
              "border-amber-100 bg-amber-50"
            )}>
              <div className="flex items-start gap-2">
                <AlertTriangle className={cn(
                  "h-4 w-4 mt-0.5 flex-shrink-0",
                  result.consultationUrgency === "urgente" ? "text-red-600" :
                  result.consultationUrgency === "semi-urgente" ? "text-amber-600" :
                  "text-amber-600"
                )} />
                <div>
                  <p className={cn(
                    "text-xs font-medium",
                    result.consultationUrgency === "urgente" ? "text-red-800" :
                    result.consultationUrgency === "semi-urgente" ? "text-amber-800" :
                    "text-amber-800"
                  )}>
                    {result.consultationUrgency === "urgente" ? 
                      "Consultation médicale urgente recommandée" :
                      result.consultationUrgency === "semi-urgente" ?
                      "Consultation médicale recommandée prochainement" :
                      "Consultation médicale recommandée"
                    }
                  </p>
                  <p className={cn(
                    "text-xs mt-1",
                    result.consultationUrgency === "urgente" ? "text-red-700" :
                    result.consultationUrgency === "semi-urgente" ? "text-amber-700" :
                    "text-amber-700"
                  )}>
                    Cette analyse ne remplace pas un avis médical professionnel.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {result.prognosis && (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
              <h5 className="text-sm font-medium text-indigo-800 mb-1 flex items-center">
                <Info className="h-4 w-4 mr-1.5" />
                Évolution probable sans traitement
              </h5>
              <p className="text-xs text-indigo-700">
                {result.prognosis}
              </p>
            </div>
          )}
          
          <div className="flex justify-end pt-2">
            <Button 
              onClick={resetAnalysis} 
              variant="ghost" 
              size="sm"
              className="text-xs flex items-center"
            >
              <RefreshCcw className="h-3 w-3 mr-1" />
              Nouvelle analyse
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
